/**
 * #593 owner control for the canonical GitHub merge route (#597/#635).
 *
 * The backend already exists: public.set_github_merge_route(jsonb) - owner AAL2, one exact
 * repository, one exact computer, node-bound. This file only shapes that one request and reads
 * the route back from the existing connector inventory (list_connectors). No new RPC, store or
 * authorization path; no GitHub credential ever passes through Command: the computer's own
 * signed-in `gh` is the credential, and the route stays `pending` until that computer proves it.
 */
import type { SupabaseClient } from '@supabase/supabase-js';
import type { ConnectorRecord } from '@brunel/shared';

/** The server's own rule for `owner/name` (it lowercases and trims before checking). */
const REPOSITORY = /^[a-z0-9_.-]{1,100}\/[a-z0-9_.-]{1,100}$/;
const PROVIDER_PREFIX = 'github.com/';

/** `owner/name` exactly as the server will store it, or null. Never guessed or completed. */
export function exactRepository(input: string): string | null {
  const value = input.trim().toLowerCase();
  return REPOSITORY.test(value) ? value : null;
}

export interface GithubMergeRouteRequest {
  readonly orgId: string;
  readonly repository: string;
  readonly nodeId: string | null;
  readonly enabled: boolean;
}

/** The exact four keys set_github_merge_route accepts. Enabling needs an exact computer. */
export function githubMergeRouteRequest(input: {
  readonly orgId: string;
  readonly repository: string;
  readonly nodeId: string | null;
  readonly enabled: boolean;
}): GithubMergeRouteRequest | null {
  const repository = exactRepository(input.repository);
  if (repository === null) return null;
  if (input.enabled && input.nodeId === null) return null;
  return { orgId: input.orgId, repository, nodeId: input.nodeId, enabled: input.enabled };
}

export type GithubMergeRouteState = 'active' | 'pending' | 'unavailable' | 'disabled' | 'unknown';

export interface GithubMergeRouteView {
  readonly connectorId: string;
  readonly repository: string;
  readonly nodeId: string | null;
  readonly state: GithubMergeRouteState;
  readonly authState: string;
  readonly healthStatus: string;
}

/** What the canonical connector row says about the route - never more. */
export function githubMergeRouteState(connector: Pick<ConnectorRecord, 'status' | 'authState' | 'healthStatus'>): GithubMergeRouteState {
  if (connector.status === 'revoked') return 'disabled';
  if (connector.status !== 'active') return 'unknown';
  if (connector.authState === 'pending') return 'pending';
  if (connector.authState === 'signed_out' || connector.healthStatus === 'unavailable' || connector.healthStatus === 'degraded') {
    return 'unavailable';
  }
  if (connector.authState === 'signed_in' && connector.healthStatus === 'healthy') return 'active';
  return 'unknown';
}

/** Only merge-capable github_repository connectors, newest state first per repository. */
export function githubMergeRoutes(connectors: readonly ConnectorRecord[]): readonly GithubMergeRouteView[] {
  return connectors
    .filter((row) => row.connectorType === 'github_repository'
      && row.providerService.startsWith(PROVIDER_PREFIX)
      && row.scopes.includes('pull_request:merge'))
    .map((row) => ({
      connectorId: row.id,
      repository: row.providerService.slice(PROVIDER_PREFIX.length),
      nodeId: row.selectedNodeId,
      state: githubMergeRouteState(row),
      authState: row.authState,
      healthStatus: row.healthStatus,
    }))
    .sort((a, b) => (a.state === 'disabled' ? 1 : 0) - (b.state === 'disabled' ? 1 : 0)
      || a.repository.localeCompare(b.repository));
}

export const GITHUB_MERGE_ROUTE_STATE_LABEL: Readonly<Record<GithubMergeRouteState, string>> = {
  active: 'Active — the computer\'s signed-in GitHub account can merge',
  pending: 'Pending — waiting for that computer to prove its signed-in GitHub account',
  unavailable: 'Unavailable — that computer reports GitHub signed out or unhealthy',
  disabled: 'Disabled',
  unknown: 'Unknown',
};

const MESSAGES: Readonly<Record<string, string>> = {
  forbidden: 'Owner verification is required to change the GitHub merge route. Verify owner and try again.',
  unauthorized: 'Sign in again to change the GitHub merge route.',
  invalid_input: 'Enter the exact repository as owner/name and choose the exact computer.',
  not_found: 'That computer no longer exists.',
};

const UNKNOWN =
  'Brunel could not confirm whether the GitHub merge route saved. The current route will be reread; the change will not be retried automatically.';

export type GithubMergeRouteSaveOutcome = 'saved' | 'rejected' | 'unknown';

export interface GithubMergeRouteSaveResult {
  readonly outcome: GithubMergeRouteSaveOutcome;
  readonly message: string | null;
  readonly connector: Partial<ConnectorRecord> | null;
}

/**
 * Exactly one network attempt of the canonical RPC. A lost response is "unknown": the caller
 * rereads the connector inventory instead of replaying the write.
 */
export async function saveGithubMergeRouteOnce(
  client: Pick<SupabaseClient, 'rpc'>,
  request: GithubMergeRouteRequest,
): Promise<GithubMergeRouteSaveResult> {
  let data: unknown;
  try {
    const response = await client.rpc('set_github_merge_route', { request });
    if (response.error) return { outcome: 'unknown', message: UNKNOWN, connector: null };
    data = response.data;
  } catch {
    return { outcome: 'unknown', message: UNKNOWN, connector: null };
  }
  const result = data as { ok?: boolean; value?: Partial<ConnectorRecord>; error?: { code?: string } } | null;
  if (result?.ok === true) return { outcome: 'saved', message: null, connector: result.value ?? null };
  const code = result?.error?.code ?? 'internal';
  return { outcome: 'rejected', message: MESSAGES[code] ?? 'The GitHub merge route change was rejected.', connector: null };
}
